import { apiFetch, serviceBase } from './http';
import type { PlanCard } from '../types';

/* ПЛАНЫ УХОДА — post-care-api за гейтвеем.

       GET {gw}/post-care-api.application/api/v1/read/patient/post-care/channel
         → PlanCardRead[]

   НИ ОДНОГО ПАРАМЕТРА, и это свойство контракта, а не упрощение: лента
   адресуется ТОЛЬКО аккаунтом из проверенного гейтвеем контекста. `patientId` в
   запросе был бы BOLA — пациент не вправе называть чужую карту и не должен
   называть свою.

   ПЛАНЫ ПРИЕЗЖАЮТ ИЗ РАЗНЫХ КЛИНИК: пациентский скоуп — это карты аккаунта, а
   не одна клиника. Поэтому в карточке печатается имя клиники: без него лента
   читается как каша из чужих друг другу планов.

   ЗАВЕРШЁННОСТЬ СЧИТАЕТ СЕРВЕР (`completed`) — это предикат «все пункты отмечены
   и их больше нуля», а не колонка. Считать её на клиенте по `checksDone ===
   checksTotal` нельзя: план без пунктов дал бы «завершён».

   ТЕЛА ПЛАНА ЗДЕСЬ НЕТ — за ним идут в `/channel/plan?channelId=`. Экрана одного
   плана в этом кабинете пока нет: макет `Кабинет пациента` показывает только
   ленту, а отдельный экран нарисован в `План ухода - пациент, десктоп`. Кнопка
   «открыть план» поэтому ведёт в никуда и об этом честно говорит. */

const POST_CARE = 'post-care-api';

export type PlansResult =
    | { state: 'ok'; plans: PlanCard[] }
    /** Раздел не подключён к этому окружению (нет гейтвея). */
    | { state: 'offline' }
    /** Гейтвей не пустил: узел доступа роли `patient` не выдан в дереве. */
    | { state: 'forbidden' }
    | { state: 'error' };

export async function readPlans(): Promise<PlansResult> {
    const base = serviceBase(POST_CARE);
    if (base === null) return { state: 'offline' };
    try {
        const res = await apiFetch(base, '/read/patient/post-care/channel');
        if (res.status === 401) return { state: 'forbidden' };
        if (res.status === 403) return { state: 'forbidden' };
        if (!res.ok) return { state: 'error' };
        const body = await res.json();
        if (!Array.isArray(body)) return { state: 'error' };
        return { state: 'ok', plans: body.map(toPlan) };
    } catch {
        // Сюда же попадает отказ CORS — из браузера он неотличим от обрыва сети.
        return { state: 'error' };
    }
}

/* Разбор с провода ЯВНЫЙ, а не `body as PlanCard[]`. Приведение типа ничего не
   проверяет: пропущенное поле стало бы `undefined` внутри разметки, и экран
   упал бы на `toFixed` или напечатал «undefined из 4». Здесь же видно, какие
   поля кабинет действительно использует. */
function toPlan(raw: unknown): PlanCard {
    const r = (raw ?? {}) as Record<string, unknown>;
    const doc = (r.doctor ?? {}) as Record<string, unknown>;
    return {
        id: str(r.id),
        title: str(r.title),
        proc: str(r.proc),
        doctor: {
            name: str(doc.name),
            initials: str(doc.initials),
            clinicName: str(doc.clinicName),
        },
        anchorAt: str(r.anchorAt),
        dayNumber: num(r.dayNumber),
        dayTotal: num(r.dayTotal),
        checksDone: num(r.checksDone),
        checksTotal: num(r.checksTotal),
        completed: r.completed === true,
        nextDay: typeof r.nextDay === 'number' ? r.nextDay : null,
        // Визит, за которым следует план. Им лента визитов и сшивается с лентой
        // планов: другого способа узнать, есть ли у записи план, у кабинета нет.
        // `null` — план отправлен из карты, без записи.
        appointmentId: typeof r.appointmentId === 'string' ? r.appointmentId : null,
    };
}

function str(v: unknown): string {
    return typeof v === 'string' ? v : '';
}
function num(v: unknown): number {
    return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}
